import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Camera, Download, RefreshCw, X } from 'lucide-react';
import { apiService, RealtimeEvent } from '../../services/api';
import { useTabActivity } from '../../contexts/TabActivityContext';
import { useRealtimeMedia } from '../../hooks/useRealtimeMedia';
import { getRealtimeErrorMessage } from '../../features/realtime/errors';
import { REALTIME_ALERT_LABELS } from '../../features/realtime/config';
import { SegmentedPlayer } from '../SegmentedPlayer';
import { SegmentedTimeline } from '../SegmentedTimeline';
import RealtimeMediaTime from './RealtimeMediaTime';
import { getCollapsibleMetadataPaths } from '../StructuredMetadataView';
import ResultDetailSidebar from '../ResultDetailSidebar';
import MetadataViewControls, { MetadataValueView, MetadataViewMode } from '../MetadataViewControls';
import { downloadJsonFile } from '../../utils/download';
import './RealtimeEventDetails.css';

export { useRealtimeMedia } from '../../hooks/useRealtimeMedia';

export const RealtimeThumbnail: React.FC<{ event: RealtimeEvent }> = ({ event }) => {
  const { url, loading } = useRealtimeMedia(event.thumbnail_url, event.thumbnail_state);
  const [failedUrl, setFailedUrl] = useState('');
  return url && url !== failedUrl
    ? <img src={url} alt={event.title} className="realtime-event-thumbnail" onError={() => setFailedUrl(url)} />
    : <div className="realtime-event-thumbnail realtime-thumbnail-pending" aria-label={loading ? 'Loading preview' : 'Preview unavailable'}>
      {loading ? <RefreshCw size={22} className="spinning" /> : <Camera size={22} />}
    </div>;
};

const RETRY_LABELS = { indexing: 'Retry search', thumbnail: 'Retry preview', storage: 'Save clip again' };

const RealtimeEventDetails: React.FC<{
  event: RealtimeEvent;
  onClose: () => void;
  onUpdated: (event: RealtimeEvent) => void;
}> = ({ event, onClose, onUpdated }) => {
  const { isActive } = useTabActivity();
  const media = useRealtimeMedia(event.clip_url, event.storage_state);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [mode, setMode] = useState<MetadataViewMode>('fields');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [retrying, setRetrying] = useState('');
  const [error, setError] = useState('');
  const segments = useMemo(() => media.url ? [{
    id: event.event_id, playbackUrl: media.url, previewGifUrl: '',
    duration: event.media_duration_seconds, start: 0, end: event.media_duration_seconds,
  }] : [], [event.event_id, event.media_duration_seconds, media.url]);

  useEffect(() => {
    setTime(0); setPlaying(true); setCollapsed(new Set()); setError('');
  }, [event.event_id]);

  const retry = async (operation: 'indexing' | 'thumbnail' | 'storage') => {
    setRetrying(operation); setError('');
    try { onUpdated(await apiService.retryRealtimeEventOperation(event.project_id, event.event_id, operation)); }
    catch (failure) { setError(getRealtimeErrorMessage(failure, 'Unable to retry. Try again shortly.')); }
    finally { setRetrying(''); }
  };
  const failedOperations = (['indexing', 'thumbnail', 'storage'] as const).filter(operation => event[`${operation}_state`] === 'failed');

  return <ResultDetailSidebar isOpen isVisible={isActive} onClose={onClose} label="Observation details" className="realtime-event-sidebar">
    <header className="search-result-header">
      <div className="search-result-header__main">
        <h3 className="search-result-header__title">{event.title}</h3>
        <div className="search-result-header__actions">
          <button type="button" className="export-segment-btn" onClick={() => downloadJsonFile(event.extracted_metadata, `${event.event_id}.json`)} title="Export observation metadata as JSON"><Download size={14} />Export</button>
          <button type="button" className="close-button" onClick={onClose} aria-label="Close observation details"><X size={20} /></button>
        </div>
      </div>
      <dl className="search-result-header__context">
        <div className="search-result-header__field"><dt>Start</dt><dd><RealtimeMediaTime value={event.captured_start_at} /></dd></div>
        <div className="search-result-header__field"><dt>End</dt><dd><RealtimeMediaTime value={event.captured_end_at} /></dd></div>
        <div className="search-result-header__field"><dt>Alert</dt><dd><span className={`realtime-alert-tag realtime-alert-${event.alert_level}`}>{REALTIME_ALERT_LABELS[event.alert_level]}</span></dd></div>
      </dl>
    </header>
    <div className="view-container">
      <div className="modal-content video-view">
        <section className="segmented-video-section" aria-label="Observation clip">
          {media.error ? <div className="full-video-error" role="alert"><AlertCircle size={28} /><span>{media.error}</span><button type="button" onClick={media.reload}>Retry</button></div>
            : segments.length ? <>
              <SegmentedPlayer segments={segments} globalTime={time} onGlobalTimeChange={setTime} playing={playing && isActive} onPlayingChange={setPlaying} />
              <SegmentedTimeline segments={segments} globalTime={time} totalDuration={event.media_duration_seconds} onChange={setTime} />
            </>
              : <div className="loading-state" role="status" aria-label="Loading clip"><RefreshCw size={20} className="spinning" /><span>Loading clip…</span></div>}
        </section>
        <section className="realtime-observation-summary">
          <p>{event.description}</p>
          {event.alert_reason && <p className={`realtime-alert-reason realtime-alert-${event.alert_level}`}>{event.alert_reason}</p>}
        </section>
        <section className="segment-details">
          <div className="segment-details-header">
            <h3>Observation details</h3>
            <MetadataViewControls mode={mode} onModeChange={setMode} sortBy="alpha" sortDirection={sortDirection}
              onSortChange={() => setSortDirection(current => current === 'asc' ? 'desc' : 'asc')}
              allExpanded={collapsed.size === 0}
              onToggleAll={() => setCollapsed(current => current.size ? new Set() : new Set(getCollapsibleMetadataPaths(event.extracted_metadata)))} />
          </div>
          <div className="metadata-display">
            <MetadataValueView mode={mode} value={event.extracted_metadata} collapsedPaths={collapsed}
              onTogglePath={path => setCollapsed(current => { const next = new Set(current); if (next.has(path)) next.delete(path); else next.add(path); return next; })}
              sortBy="alpha" sortDirection={sortDirection} />
          </div>
        </section>
        {failedOperations.length > 0 && <div className="realtime-observation-actions">{failedOperations.map(operation => <button key={operation} type="button" className="export-segment-btn" disabled={Boolean(retrying)} onClick={() => void retry(operation)}><RefreshCw size={14} className={retrying === operation ? 'spinning' : ''} />{RETRY_LABELS[operation]}</button>)}</div>}
        {error && <div className="search-result-notice" role="alert"><AlertCircle size={16} /><span>{error}</span></div>}
      </div>
    </div>
  </ResultDetailSidebar>;
};
export default RealtimeEventDetails;
